<template>
  <form method="post" @submit.prevent="onSend" novalidate>
    <form-wrapper :validator="$v.form">
      <div class="buttons is-right">
        <b-button
          :loading="sending"
          style="width: 100px"
          @click="onCancel"
          tabindex="10"
        >
          {{ $t('attributes.cancel') }}
        </b-button>
        <b-button
          :loading="sending"
          :type="mode === 'new' ? 'is-primary' : 'is-warning'"
          native-type="submit"
          style="width: 100px"
          tabindex="10"
        >
          {{ mode === 'new' ? $t('attributes.add') : $t('attributes.update') }}
        </b-button>
      </div>

      <div class="columns">
        <div class="column is-4" style="border-right: #1e7c9c dotted 2px;">
          <form-group name="is_public">
            <b-switch v-model="form.is_public" left-label>
              {{ $t('attributes.gift_application_public') }}
            </b-switch>
          </form-group>

          <form-group
            v-if="isFit"
            name="user_id"
            attribute="attributes.gift_user"
            :label="$t('attributes.gift_user')"
            label-position="on-border"
          >
            <b-input
              v-if="form.status !== 'new'"
              v-model="form.user_info"
              name="user_id"
              disabled
            ></b-input>
            <b-autocomplete
              v-else
              v-model="userSearch"
              name="search_user"
              field="display_name"
              keep-first
              :data="users"
              :loading="isLoadingUsers"
              :custom-formatter="
                u => `${u.identity} ${u.display_name} ${u.phone_number}`
              "
              placeholder="搜尋會員名稱/身分證號碼/電話"
              :open-on-focus="false"
              @typing="loadUsers"
              @select="onSelectUser"
            >
              <template #default="props">
                <p>
                  {{ props.option.identity }} -
                  <span>{{ props.option.display_name }}</span> -
                  <span>{{ props.option.phone_number }}</span>
                </p>
              </template>
              <template #empty>
                {{
                  userSearch
                    ? isLoadingUsers
                      ? $t('attributes.searching')
                      : $t('attributes.not_found')
                    : '搜尋會員名稱/身分證號碼/電話'
                }}
              </template>
            </b-autocomplete>
          </form-group>

          <div v-if="isGroup" class="columns mb-1">
            <div class="column">
              <form-group
                name="group_code"
                attribute="attributes.gift_group"
                :label="$t('attributes.cowell') + $t('attributes.group_code')"
                label-position="on-border"
              >
                <b-autocomplete
                  v-model="groupSearch"
                  name="group_code"
                  field="code"
                  keep-first
                  :data="groups"
                  :loading="isLoadingGroups"
                  :placeholder="$t('message.please_input_group_code')"
                  :open-on-focus="false"
                  @typing="loadGroups"
                  @select="onSelectGroup"
                >
                  <template #default="props" style="width: 150px;">
                    <p>
                      {{ props.option.code }} -
                      <!-- eslint-disable-next-line-->
                      <span v-html="props.option.name"></span>
                    </p>
                  </template>
                  <template #empty>
                    {{
                      groupSearch
                        ? isLoadingGroups
                          ? $t('attributes.searching')
                          : $t('attributes.not_found')
                        : $t('message.please_input_group_code')
                    }}
                  </template>
                </b-autocomplete>
              </form-group>
            </div>

            <div class="column">
              <form-group
                name="group_departure_date"
                attribute="attributes.departure_date"
                :label="$t('attributes.departure_date')"
                label-position="on-border"
              >
                <b-input
                  v-model="form.group_departure_date"
                  name="group_departure_date"
                  :readonly="true"
                  disabled
                ></b-input>
              </form-group>
            </div>
          </div>
          <form-group
            v-if="isGroup"
            name="group_name"
            attribute="attributes.gift_group"
            :label="$t('attributes.group_name')"
            label-position="on-border"
          >
            <b-input
              v-model="form.group_name"
              name="group_name"
              expanded
              :readonly="true"
              disabled
            ></b-input>
          </form-group>

          <form-group
            name="remark"
            :label="$t('attributes.gift_application_remark')"
            label-position="on-border"
          >
            <b-input
              v-model="form.remark"
              name="remark"
              maxlength="500"
              :has-counter="false"
              :placeholder="
                $t('message.please_input') +
                  $t('attributes.gift_application_remark')
              "
              expanded
            ></b-input>
          </form-group>

          <div>
            <p>已選擇的贈品</p>
            <b-table
              :data="selectedGifts"
              :mobile-cards="false"
              narrowed
              sticky-header
              height="calc(100vh - 485px)"
              @select="onSelectGift"
            >
              <b-table-column
                field="name"
                :label="$t('attributes.name')"
                header-class="nowrap"
                v-slot="props"
              >
                {{ props.row.full_name }}
              </b-table-column>

              <b-table-column
                field="net_price"
                :label="$t('attributes.gift_net_price')"
                numeric
                header-class="nowrap"
                v-slot="props"
              >
                <span :class="{ 'is-strike': isFree(props.row) }">
                  {{ props.row.net_price }}
                </span>
              </b-table-column>

              <b-table-column
                field="quantity"
                :label="$t('attributes.gift_current_quantity')"
                numeric
                header-class="nowrap"
                v-slot="props"
              >
                {{ props.row.quantity }}
              </b-table-column>

              <b-table-column
                field="application_quantity"
                :label="$t('attributes.gift_application_request_quantity')"
                centered
                header-class="nowrap"
                v-slot="props"
              >
                {{ props.row.apply_quantity }}
              </b-table-column>
              <b-table-column
                field="amount"
                :label="$t('attributes.gift_amount')"
                numeric
                header-class="nowrap"
                v-slot="props"
              >
                {{ formatCurrency(calculateAmount(props.row)) }}
              </b-table-column>
            </b-table>
          </div>
        </div>
        <div class="column">
          <div
            class="px-2 pb-1 is-flex is-flex-direction-row is-justify-content-space-between is-align-items-center"
          >
            <div class="is-flex is-flex-direction-row">
              <b-input
                v-model="giftFilter"
                placeholder="過濾贈品"
                type="search"
                icon="magnify"
                clearable
                style="width: 240px; margin-right: 12px;"
              ></b-input>
              <b-switch v-model="sortDepartment">
                按照部門排序
              </b-switch>
            </div>
            <p class="is-size-5 has-text-weight-semibold has-text-warning-dark">
              {{ $t('attributes.gift_application_amount') }}:
              {{ formatCurrency(calculateTotalAmount()) }}
            </p>
          </div>
          <b-table
            :data="filterGifts"
            :loading="loadingGifts"
            :mobile-cards="false"
            narrowed
            sticky-header
            height="calc(100vh - 302px)"
          >
            <b-table-column
              field="id"
              :visible="false"
              :label="$t('attributes.id')"
              width="75"
              centered
              v-slot="props"
            >
              {{ props.row.id }}
            </b-table-column>

            <b-table-column
              field="picture"
              :label="$t('attributes.picture')"
              width="60"
              centered
              header-class="nowrap"
              v-slot="props"
            >
              <figure v-if="props.row.picture" class="image">
                <a
                  :href="GiftImage(props.row.picture)"
                  title="贈品圖片"
                  @click.prevent="onClickImage(props.row)"
                >
                  <img
                    :src="GiftImage(props.row.picture)"
                    :alt="$t('attributes.picture')"
                    style="width: 80px; margin: auto;"
                  />
                </a>
              </figure>
              <p v-else class="has-text-danger has-text-weight-medium">無</p>
            </b-table-column>

            <b-table-column
              field="name"
              :label="$t('attributes.name')"
              header-class="nowrap"
              v-slot="props"
            >
              <span>{{ props.row.full_name }}</span>
            </b-table-column>

            <b-table-column
              field="net_price"
              :label="$t('attributes.gift_net_price')"
              width="80"
              numeric
              header-class="nowrap"
              v-slot="props"
            >
              <span :class="{ 'is-strike': isFree(props.row) }">
                {{ props.row.net_price }}
              </span>
            </b-table-column>

            <b-table-column
              field="quantity"
              :label="$t('attributes.gift_current_quantity')"
              width="80"
              numeric
              header-class="nowrap"
              v-slot="props"
            >
              {{ props.row.quantity }}
            </b-table-column>

            <b-table-column
              field="application_quantity"
              :label="$t('attributes.gift_application_request_quantity')"
              width="120"
              centered
              header-class="nowrap"
              v-slot="props"
            >
              <b-input
                v-model.number="props.row.apply_quantity"
                name="quantity"
                type="number"
                min="0"
                :max="props.row.quantity"
                rounded
                :disabled="!props.row.can_apply"
                :placeholder="
                  $t('message.please_input') + $t('attributes.gift_quantity')
                "
              ></b-input>
            </b-table-column>
            <b-table-column
              field="amount"
              :label="$t('attributes.gift_amount')"
              width="80"
              numeric
              header-class="nowrap"
              v-slot="props"
            >
              <span v-if="props.row.can_apply">
                {{ formatCurrency(calculateAmount(props.row)) }}
              </span>
              <span v-else class="has-text-danger has-text-weight-bold ">
                未過帳
              </span>
            </b-table-column>

            <template #empty>
              <section class="section">
                <div
                  v-if="!loadingGifts"
                  class="content has-text-grey has-text-centered"
                >
                  <p>
                    <b-icon icon="emoticon-sad" size="is-large" />
                  </p>
                </div>
              </section>
            </template>
          </b-table>
        </div>
      </div>
    </form-wrapper>

    <b-modal has-modal-card :active.sync="isImageModalActive">
      <p class="image">
        <img
          style="max-width: 450px; max-height: 800px; text-align: center;"
          :src="GiftImage(showPicture)"
          alt=""
        />
      </p>
    </b-modal>

    <b-loading
      :active.sync="isLoading"
      :is-full-page="false"
      :can-cancel="false"
    ></b-loading>
  </form>
</template>

<script>
import { maxLength, required } from 'vuelidate/lib/validators'
import { GIFTS_API, USER_API, TRAVEL_GROUP_BY_COWELL } from '@/utils/apis'
import { GIFT_REVIEW_APPLY_TYPES } from '@/utils/constants'
import debounce from 'lodash/debounce'
import notifyService from '@/services/notifyService'

export default {
  name: 'ApplicationCreateForm',

  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return [
          GIFT_REVIEW_APPLY_TYPES.FIT,
          GIFT_REVIEW_APPLY_TYPES.GROUP
        ].includes(value)
      }
    },
    mode: {
      type: String,
      required: true,
      validator(value) {
        return ['new', 'modify'].includes(value)
      }
    },
    sales_code: {
      type: String,
      required: true
    },
    sales_department: {
      type: String,
      required: true
    },
    application: {
      type: Object,
      required: true
    },
    applyGifts: {
      type: Array,
      required: true
    },
    sending: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      isFit: this.type === GIFT_REVIEW_APPLY_TYPES.FIT,
      isGroup: this.type === GIFT_REVIEW_APPLY_TYPES.GROUP,

      giftFilter: null,
      sortDepartment: false,
      loadingGifts: false,
      gifts: [],

      isLoadingUsers: false,
      users: [],
      userSearch: null,

      isLoadingGroups: false,
      groups: [],
      groupSearch: null,

      operator: `[${this.$store.getters.empCode}] ${this.$store.getters.username}`,

      form: this.extractForm(this.application),

      isImageModalActive: false,
      showPicture: null
    }
  },
  computed: {
    isLoading() {
      return this.loadingGifts || this.isLoadingGroups || this.sending
    },
    filterGifts() {
      let gifts = this.gifts.slice()

      if (this.giftFilter) {
        gifts = gifts.filter(g => g.full_name.includes(this.giftFilter))
      }

      if (this.sortDepartment) {
        gifts = gifts.sort(function(a, b) {
          if (!a.department_name) return +1
          if (!b.department_name) return -1
          return a.department_name.localeCompare(b.department_name, 'zh-Hant')
        })
      }

      return gifts
    },
    selectedGifts() {
      let gifts = this.gifts.filter(g => g.apply_quantity > 0)

      if (this.sortDepartment) {
        gifts = gifts.sort(function(a, b) {
          if (!a.department_name) return +1
          if (!b.department_name) return -1
          return a.department_name.localeCompare(b.department_name, 'zh-Hant')
        })
      }

      return gifts
    }
  },
  watch: {
    application(application) {
      this.userSearch = null
      this.groupSearch = null
      this.giftFilter = null
      this.form = this.extractForm(application)
      this.$v.$reset()
      this.loadGiftsAsync()
    },
    applyGifts() {
      this.loadGiftsAsync()
    }
  },
  mounted() {
    this.loadGiftsAsync()
  },
  validations() {
    if (this.type === GIFT_REVIEW_APPLY_TYPES.FIT) {
      return {
        form: {
          is_public: { required },
          user_id: {},
          gifts: { required },
          remark: { maxLength: maxLength(500) }
        }
      }
    } else {
      return {
        form: {
          is_public: { required },
          group_code: { required },
          group_departure_date: { required },
          group_name: { required },
          gifts: { required },
          remark: { maxLength: maxLength(500) }
        }
      }
    }
  },
  methods: {
    extractForm(application) {
      const user_info = application.user_id
        ? `${application.user_identity} - ${application.user_name} ${application.user_phone}`
        : '自購'

      return {
        id: application.id,
        is_edit: application.is_edit,
        status: application.status,
        is_public: application.is_public,
        user_id: application.user_id,
        user_identity: application.user_identity,
        user_name: application.user_name,
        user_phone: application.user_phone,
        user_info: user_info,
        group_code: application.group_code,
        group_name: application.group_name,
        group_departure_date: application.group_departure_date,
        gifts: application.gifts,
        amount: application.amount,
        remark: application.remark
      }
    },
    async loadGiftsAsync() {
      try {
        const api = `${GIFTS_API}/list-apply`
        const params = {
          search: null
        }

        this.loadingGifts = true
        const res = await this.axios.get(api, { params })
        this.gifts = res.data.data.map(gift => {
          const applyGift = this.applyGifts.find(g => g.id === gift.id)
          let quantity = applyGift
            ? gift.quantity - applyGift.quantity
            : gift.quantity
          if (quantity < 0) quantity = 0

          const gf = this.form.gifts.find(g => g.id === gift.id)
          const apply_quantity = gf ? gf.quantity : 0

          return {
            ...gift,
            can_apply: gift.posts.length > 0,
            quantity: quantity,
            apply_quantity: apply_quantity
          }
        })
        this.amount = this.calculateTotalAmount()
      } catch (e) {
        this.gifts = []

        notifyService.fetch_error('gift', e)
      } finally {
        this.loadingGifts = false
      }
    },
    onSelectGift(item) {
      this.giftFilter = item.name
    },
    async loadGroupsAsync() {
      const api = `${TRAVEL_GROUP_BY_COWELL}/auto-complete`
      const params = {
        search: this.groupSearch
      }

      this.isLoadingGroups = true
      this.isloading = true
      try {
        const res = await this.axios.get(api, { params })
        this.groups = res.data.data
      } catch (e) {
        this.groups = []

        notifyService.fetch_error('group', e)
      } finally {
        this.isLoadingGroups = false
        this.isloading = false
      }
    },
    loadGroups: debounce(function() {
      if (!this.groupSearch) {
        this.groups = []
        return
      }

      this.loadGroupsAsync()
    }, 1000),
    onSelectGroup(option) {
      this.form.group_code = option && option.code
      this.form.group_name = option && option.name
      this.form.group_departure_date =
        option && this.formatDate(option.departure_date)
    },
    async loadUsersAsync() {
      const api = `${USER_API}/search`
      const params = {
        search: this.userSearch
      }

      this.isLoadingUsers = true
      try {
        const res = await this.axios.get(api, { params })
        this.users = res.data.data
      } catch (e) {
        this.users = []

        notifyService.fetch_error('user', e)
      } finally {
        this.isLoadingUsers = false
      }
    },
    loadUsers: debounce(function() {
      if (!this.userSearch) {
        this.users = []
        return
      }

      this.loadUsersAsync()
    }, 1000),
    onSelectUser(option) {
      this.form.user_id = option && option.id
      this.form.user_identity = option && option.identity
      this.form.user_name = option && option.display_name
      this.form.user_phone = option && option.user_phone
      this.form.user_info = option
        ? `${option.identity} - ${option.display_name} ${option.phone_number}`
        : '自購'
    },
    onCancel() {
      this.$emit('cancel')
    },
    async onSend() {
      const gifts = this.gifts.filter(
        g => g.apply_quantity < 0 || g.apply_quantity > g.quantity
      )

      if (gifts.length > 0) {
        const gift = gifts[0]
        const message = `${gift.full_name} ${
          gift.apply_quantity < 0
            ? '申請數量不可小於零'
            : '申請數量超過庫存數量'
        }`
        return notifyService.error(message)
      }

      this.form.gifts = this.gifts
        .filter(g => g.apply_quantity > 0)
        .map(g => ({
          id: g.id,
          quantity: g.apply_quantity
        }))
      this.form.amount = this.calculateTotalAmount()

      if (this.form.gifts.length === 0) {
        return notifyService.error('請設定贈品及數量')
      }

      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    },

    isFree(gift) {
      return this.form.is_public || gift.department_no === this.sales_department
    },
    calculateAmount(gift) {
      if (gift.apply_quantity < 0) {
        return 0
      }

      return this.isFree(gift) ? 0 : gift.net_price * gift.apply_quantity
    },
    calculateTotalAmount() {
      return this.gifts.reduce(
        (sum, gift) => sum + this.calculateAmount(gift),
        0
      )
    },
    onClickImage(gift) {
      this.showPicture = gift.picture
      this.isImageModalActive = true
    },
    chkGroupCode() {
      this.form.group_code = this.form.group_code
        .trim()
        .replace(/[\u4e00-\u9fa5]/gi, '')
    }
  }
}
</script>
